import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    AbilityLookup,
    Action,
    ActionUtils,
    BaseFeat,
    characterActions,
    CharClass,
    ClassFeature,
    ClassFeatureUtils,
    ClassUtils,
    DataOriginRefData,
    InfusionChoice,
    InfusionChoiceUtils,
    RuleData,
    rulesEngineSelectors,
    SnippetData,
    Spell,
    SpellUtils,
} from '@dndbeyond/character-rules-engine/es';
import { CharacterTheme } from '@dndbeyond/character-rules-engine/es';

import { ClassFeatureSnippet } from '../../../../CharacterSheet/components/FeatureSnippet';
import { sidebarActions } from '../../../actions';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils } from '../../../utils';
import { PaneIdentifiersClassFeature } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    classes: Array<CharClass>;
    identifiers: PaneIdentifiersClassFeature | null;
    feats: Array<BaseFeat>;
    snippetData: SnippetData;
    ruleData: RuleData;
    abilityLookup: AbilityLookup;
    dataOriginRefData: DataOriginRefData;
    isReadonly: boolean;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
interface State {
    charClass: CharClass | null;
    classFeature: ClassFeature | null;
}
class ClassFeaturePane extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { classes, identifiers } = this.props;

        if (identifiers !== prevProps.identifiers || classes !== prevProps.classes) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { classes, identifiers } = props;

        let foundCharClass: CharClass | null | undefined = null;
        let foundClassFeature: ClassFeature | null | undefined = null;

        if (identifiers !== null) {
            foundCharClass = classes.find(
                (charClass) => identifiers.classMappingId === ClassUtils.getMappingId(charClass),
            );
            if (foundCharClass) {
                foundClassFeature = foundCharClass.classFeatures.find(
                    (feature) => identifiers.id === ClassFeatureUtils.getId(feature),
                );
            }
        }

        return {
            charClass: foundCharClass ? foundCharClass : null,
            classFeature: foundClassFeature ? foundClassFeature : null,
        };
    };

    handleInfusionChoiceShow = (infusionChoice: InfusionChoice): void => {
        const { dispatch } = this.props;
        const choiceKey = InfusionChoiceUtils.getKey(infusionChoice);
        if (choiceKey !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.INFUSION_CHOICE,
                    PaneIdentifierUtils.generateInfusionChoice(choiceKey),
                ),
            );
        }
    };

    handleActionUseSet = (action: Action, uses: number): void => {
        const { dispatch } = this.props;

        const id = ActionUtils.getId(action);
        const entityTypeId = ActionUtils.getEntityTypeId(action);
        if (id !== null && entityTypeId !== null) {
            dispatch(characterActions.actionUseSet(id, entityTypeId, uses, ActionUtils.getDataOriginType(action)));
        }
    };

    handleSpellUseSet = (spell: Spell, uses: number): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        const mappingEntityTypeId = SpellUtils.getMappingEntityTypeId(spell);
        const dataOriginType = SpellUtils.getDataOriginType(spell);

        if (mappingId && mappingEntityTypeId && dataOriginType) {
            dispatch(characterActions.spellUseSet(mappingId, mappingEntityTypeId, uses, dataOriginType));
        }
    };

    handleSpellDetailShow = (spell: Spell): void => {
        const { dispatch } = this.props;

        const mappingId = SpellUtils.getMappingId(spell);
        if (mappingId !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.CHARACTER_SPELL_DETAIL,
                    PaneIdentifierUtils.generateCharacterSpell(mappingId),
                ),
            );
        }
    };

    handleClassFeatureShow = (feature: ClassFeature, charClass: CharClass): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryPush(
                PaneComponentEnum.CLASS_FEATURE_DETAIL,
                PaneIdentifierUtils.generateClassFeature(
                    ClassFeatureUtils.getId(feature),
                    ClassUtils.getMappingId(charClass),
                ),
            ),
        );
    };

    handleActionShow = (action: Action): void => {
        const { dispatch } = this.props;

        const mappingId = ActionUtils.getMappingId(action);
        const mappingEntityTypeId = ActionUtils.getMappingEntityTypeId(action);

        if (mappingId !== null && mappingEntityTypeId !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.ACTION,
                    PaneIdentifierUtils.generateAction(mappingId, mappingEntityTypeId),
                ),
            );
        }
    };

    render() {
        const { classFeature, charClass } = this.state;

        const { feats, abilityLookup, snippetData, ruleData, dataOriginRefData, isReadonly, proficiencyBonus, theme } =
            this.props;

        if (charClass === null || classFeature === null) {
            return <PaneInitFailureContent />;
        }

        return (
            <div
                className="ct-class-feature-pane"
                key={`${ClassUtils.getMappingId(charClass)}-${ClassFeatureUtils.getId(classFeature)}`}
            >
                <SidebarHeader
                    parent={ClassUtils.getName(charClass)}
                    previewImage={ClassUtils.getPortraitUrl(charClass)}
                >
                    {ClassFeatureUtils.getName(classFeature)}
                </SidebarHeader>
                <ClassFeatureSnippet
                    charClass={charClass}
                    feature={classFeature}
                    onActionUseSet={this.handleActionUseSet}
                    onActionClick={this.handleActionShow}
                    onSpellClick={this.handleSpellDetailShow}
                    onSpellUseSet={this.handleSpellUseSet}
                    onFeatureClick={this.handleClassFeatureShow}
                    onInfusionChoiceClick={this.handleInfusionChoiceShow}
                    showHeader={false}
                    showDescription={true}
                    feats={feats}
                    snippetData={snippetData}
                    ruleData={ruleData}
                    abilityLookup={abilityLookup}
                    dataOriginRefData={dataOriginRefData}
                    isReadonly={isReadonly}
                    proficiencyBonus={proficiencyBonus}
                    theme={theme}
                />
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        classes: rulesEngineSelectors.getClasses(state),
        feats: rulesEngineSelectors.getBaseFeats(state),
        snippetData: rulesEngineSelectors.getSnippetData(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        abilityLookup: rulesEngineSelectors.getAbilityLookup(state),
        dataOriginRefData: rulesEngineSelectors.getDataOriginRefData(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
    };
}

export default connect(mapStateToProps)(ClassFeaturePane);
